import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsService } from '../../services/settingsService';
import toast from 'react-hot-toast';
import { Cog6ToothIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SystemConfig = () => {
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  const { data, isLoading } = useQuery(
    'system-config',
    () => settingsService.getSystemConfig()
  );

  const updateMutation = useMutation(
    ({ key, value, description }) => settingsService.updateSystemConfig(key, value, description),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('system-config');
        setEditingKey(null);
        toast.success('Configuration updated');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Update failed');
      }
    }
  );

  const configData = data?.data || {};

  const configCategories = {
    'system': 'System Settings',
    'loan': 'Loan Settings',
    'savings': 'Savings Settings',
    'interest': 'Interest Rate Settings',
    'notification': 'Notification Settings',
    'security': 'Security Settings'
  };

  const handleUpdate = (key, currentValue, description) => {
    updateMutation.mutate({
      key: key,
      value: editValue,
      description: description
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">System Configuration</h2>
        <p className="text-gray-600">Manage system-wide settings and parameters</p>
      </div>

      {Object.entries(configCategories).map(([category, categoryName]) => {
        const configs = configData[category] || [];
        if (configs.length === 0) return null;

        return (
          <div key={category} className="mb-6 border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold">{categoryName}</h3>
            </div>
            <div className="divide-y">
              {configs.map((config) => (
                <div key={config.config_key} className="px-4 py-3 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {config.config_key.split('_').map(word =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </div>
                      {config.description && (
                        <p className="text-sm text-gray-500 mt-1">{config.description}</p>
                      )}
                      <div className="mt-2">
                        {editingKey === config.config_key ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="px-3 py-1 border rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
                            />
                            <button
                              onClick={() => handleUpdate(config.config_key, config.config_value, config.description)}
                              className="text-green-600 hover:text-green-800"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setEditingKey(null)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <code className="px-2 py-1 bg-gray-100 rounded text-sm">
                              {config.config_value}
                            </code>
                            <button
                              onClick={() => {
                                setEditingKey(config.config_key);
                                setEditValue(config.config_value);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {Object.keys(configData).length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Cog6ToothIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No configuration settings found</p>
        </div>
      )}
    </div>
  );
};

export default SystemConfig;